import { Card, CardBody, Select, SelectItem } from '@heroui/react';
import React from 'react';

import { BaseButton } from '../../../../../components/BaseButton';

/**
 * Props for OrdersTablePagination
 */
interface OrdersTablePaginationProps {
  /** Whether there are more results to load */
  hasMore: boolean;
  /** Whether we can go back to previous page */
  hasPrevious: boolean;
  /** Callback for next page */
  onNext: () => void;
  /** Callback for previous page */
  onPrevious: () => void;
  /** Current page size */
  pageSize: number;
  /** Callback when page size changes */
  onPageSizeChange: (size: number) => void;
  /** Whether data is loading */
  isLoading?: boolean;
  /** Number of items on current page */
  currentPageCount: number;
}

const PAGE_SIZE_OPTIONS = [
  { value: '10', label: '10 per page' },
  { value: '25', label: '25 per page' },
  { value: '50', label: '50 per page' },
  { value: '100', label: '100 per page' },
];

/**
 * Cursor-based pagination component for orders table
 */
export const OrdersTablePagination: React.FC<OrdersTablePaginationProps> = ({
  hasMore,
  hasPrevious,
  onNext,
  onPrevious,
  pageSize,
  onPageSizeChange,
  isLoading = false,
  currentPageCount,
}) => {
  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSize = parseInt(e.target.value, 10);
    if (!Number.isNaN(newSize)) {
      onPageSizeChange(newSize);
    }
  };

  return (
    <Card className="mt-6">
      <CardBody>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-3">
            <span className="text-sm text-default-600">
              Showing{' '}
              <span className="font-medium">
                {isLoading ? '...' : currentPageCount}
              </span>{' '}
              orders
            </span>

            <Select
              size="sm"
              variant="bordered"
              selectedKeys={[pageSize.toString()]}
              onChange={handlePageSizeChange}
              className="w-40"
              aria-label="Page size"
              isDisabled={isLoading}
              classNames={{
                trigger: 'h-8',
              }}
            >
              {PAGE_SIZE_OPTIONS.map((option) => (
                <SelectItem key={option.value}>{option.label}</SelectItem>
              ))}
            </Select>
          </div>

          <div className="flex gap-2">
            <BaseButton
              variant="bordered"
              size="sm"
              onClick={onPrevious}
              disabled={!hasPrevious || isLoading}
            >
              Previous
            </BaseButton>
            <BaseButton
              variant="bordered"
              size="sm"
              onClick={onNext}
              disabled={!hasMore || isLoading}
            >
              Next
            </BaseButton>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};
